import { useRef, useEffect, useState, RefObject, CSSProperties } from 'react'

// taken from the CSS style >.dpl-functional-tag-listbox
const MAX_LISTBOX_HEIGHT = 228

/** Minimum listbox width in pixels */
const MIN_LISTBOX_WIDTH = 195

export const getListBoxHeight = (listBoxElement?: Element, tagHeight?: number) => {
  if (listBoxElement && tagHeight) {
    // listBoxElement.clientHeight is unavailable because it's hidden at the time this function is called
    // Use the number of options and the size of each individual option to calculate
    const numberOfTags = listBoxElement.children.length
    return Math.min(numberOfTags * tagHeight, MAX_LISTBOX_HEIGHT)
  }
  return 0
}

/**
 * Returns the coordinates/offsets for dropdown position of a ListBox element
 * @param targetDom the element triggering the appearance of the ListBox
 */
export const dropdownPosition = (targetDom: HTMLElement) => {
  const {
    bottom: targetBottom,
    height,
    left: targetLeft,
    top: targetTop,
    width,
  } = targetDom.getBoundingClientRect()
  const windowInnerHeight = window.innerHeight
  const [, listBoxElement] = targetDom?.children || []
  const listBoxHeight = getListBoxHeight(listBoxElement, height)

  /*
   * if the bottom of the listBox surpasses the bottom of the window
   * we want to display the listbox on top instead of below
   */
  // prettier-ignore
  const showListOnTop: boolean = windowInnerHeight < (targetBottom + listBoxHeight)

  const top = showListOnTop
    ? // height / 2 is the padding between the functional tag button and the listbox
      // prettier-ignore
      Math.round(targetTop - listBoxHeight - (height / 2))
    : Math.round(targetTop + height)

  /**
   * If the right side of the listBox overflows past the right of the window,
   * move listbox to the left
   */
  // prettier-ignore
  const showListLeft = window.innerWidth < (targetLeft + MIN_LISTBOX_WIDTH)
  const left = showListLeft ? targetLeft - (MIN_LISTBOX_WIDTH - width) : targetLeft

  return {
    top,
    left,
    width,
    minWidth: MIN_LISTBOX_WIDTH,
    display: 'block',
  }
}

interface IHTMLTargetElement extends HTMLElement {
  scrollIntoViewIfNeeded?: Function
}

const overflowRegex = /(auto|scroll)/
const getScrollParent = (node?: IHTMLTargetElement): IHTMLTargetElement | undefined => {
  if (!node) return undefined

  const overflowY = node instanceof HTMLElement ? window.getComputedStyle(node).overflowY || '' : ''
  if (overflowRegex.test(overflowY)) {
    return node
  }
  return getScrollParent(node.parentNode as IHTMLTargetElement)
}

const scrollIfNeeded = (node?: IHTMLTargetElement, scrollParent?: IHTMLTargetElement) => {
  if (!node) return

  if (node.scrollIntoViewIfNeeded) {
    node.scrollIntoViewIfNeeded()
  } else if (scrollParent) {
    const { height: parentHeight, top: offset } = scrollParent.getBoundingClientRect()
    const { height, top } = node.getBoundingClientRect()
    const nodeEndPosition = top + height - offset
    if (nodeEndPosition > parentHeight) {
      scrollParent.scrollTop += nodeEndPosition - parentHeight
    }
  }
}

// TODO: Return to this to update to an object param
export const useDropdownPosition = (
  isOpen: boolean,
  close: () => any,
  ref?: RefObject<HTMLInputElement>,
  relyOnTargetParent = false,
  keepDimensions = false,
  useMinWidth = false,
  autocomplete = false
): [RefObject<HTMLInputElement>, CSSProperties] => {
  const baseRef = useRef(null)
  const targetRef = ref || baseRef
  const [style, setStyle] = useState({})

  useEffect(() => {
    const dropdownTarget = (relyOnTargetParent && targetRef.current
      ? targetRef.current.parentNode
      : targetRef.current) as IHTMLTargetElement
    const scrollParent = getScrollParent(
      dropdownTarget ? (dropdownTarget.parentNode as IHTMLTargetElement) : undefined
    )

    if (isOpen) {
      scrollIfNeeded(dropdownTarget, scrollParent)
      const { display, left, minWidth, top, width } = dropdownPosition(dropdownTarget)
      const theWidth = autocomplete ? 250 : minWidth
      setStyle({
        transform: `translate3d(${left}px, ${top}px, 0)`,
        ...(keepDimensions
          ? {}
          : {
              width,
              minWidth: useMinWidth ? theWidth : undefined,
              display,
            }),
      })
    } else {
      setStyle({ display: 'none' })
    }

    const scrollTarget = scrollParent || global
    setTimeout(() => {
      if (isOpen) {
        scrollTarget.addEventListener('scroll', close)
        global.addEventListener('resize', close)
      }
    }, 100)

    return () => {
      scrollTarget.removeEventListener('scroll', close)
      global.removeEventListener('resize', close)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return [targetRef, style]
}
